/* זיכרון בתי עסק: מסווג לפי ההיסטוריה במקום לשאול את המודל שוב על אותו עסק.
   נגזר מהפעולות השמורות, ולכן לומד לבד מכל תיקון ידני. */
import { S, memo } from './state.js';
import { mNorm, reverseKeepRuns, rtlScore } from '../lib/text.js';
import { absAmt } from '../lib/format.js';
import { cat } from './categories.js';
import { isExcluded, getTxSrc } from './tx.js';
import { keyAdd } from './periods.js';
import { recCore } from './recurring.js';

/** שם עסק מנורמל ← הקטגוריה שנבחרה לו הכי הרבה פעמים */
export function merchantMemory() {
  return memo('merchantMemory', () => {
    const map = {};
    S.tx.forEach((t) => {
      if (!t.description || !t.cat) return;
      const k = mNorm(t.description);
      if (k.length < 3) return;
      const e = (map[k] = map[k] || { counts: {}, types: {} });
      e.counts[t.cat] = (e.counts[t.cat] || 0) + 1;
      const ty = t.type || 'expense';
      e.types[ty] = (e.types[ty] || 0) + 1;
    });
    const out = {};
    Object.keys(map).forEach((k) => {
      const e = map[k];
      let best = null, bestN = 0, total = 0, bt = null, btN = 0;
      Object.keys(e.counts).forEach((c) => { total += e.counts[c]; if (e.counts[c] > bestN) { bestN = e.counts[c]; best = c; } });
      Object.keys(e.types).forEach((t) => { if (e.types[t] > btN) { btN = e.types[t]; bt = t; } });
      out[k] = { cat: best, type: bt, n: total, agree: bestN / total };
    });
    return out;
  });
}

/** העסקים המוכרים ביותר, כרמז לפרומפט, כדי שהמודל יהיה עקבי עם ההיסטוריה */
export function merchantHints(limit = 60) {
  const mem = merchantMemory();
  const keys = Object.keys(mem).filter((k) => mem[k].n >= 2 && mem[k].agree >= 0.6).sort((a, b) => mem[b].n - mem[a].n).slice(0, limit);
  return keys.map((k) => '  ' + k + ' -> ' + mem[k].cat).join('\n');
}

/* שם קנוני: יצואנים שוברים גרש ומרכאות ל-? ומקצצים ל-20 תווים, ולכן אותו עסק מופיע בכמה איותים */
function canonicalNames(items) {
  const groups = {};
  const add = (d) => {
    if (!d) return;
    const k = mNorm(d);
    if (k.length < 3) return;
    (groups[k] = groups[k] || []).push(d);
  };
  S.tx.forEach((t) => add(t.description));
  items.forEach((it) => add(it.description));
  const best = {};
  Object.keys(groups).forEach((k) => {
    const cnt = {};
    groups[k].forEach((n) => (cnt[n] = (cnt[n] || 0) + 1));
    let winner = null, score = -1;
    Object.keys(cnt).forEach((n) => {
      const s = cnt[n] * 10 + n.length - (n.indexOf('?') >= 0 ? 1000 : 0);
      if (s > score) { score = s; winner = n; }
    });
    best[k] = winner;
  });
  let changed = 0;
  items.forEach((it) => {
    const k = mNorm(it.description);
    if (best[k] && best[k] !== it.description) { it.description = best[k]; changed++; }
  });
  return changed;
}

/** מחיל את הזיכרון על תוצאות סריקה: עסק מוכר מקבל את הקטגוריה מהעבר,
    ועסק לא מוכר שחוזר בתוך אותה סריקה מקבל קטגוריה אחידה (הכרעת רוב). משנה את items במקום */
export function applyMerchantMemory(items) {
  if (!items || !items.length) return { fromMemory: 0, unified: 0, canon: 0 };
  const canon = canonicalNames(items);
  const mem = merchantMemory(), memKeys = Object.keys(mem);
  let fromMemory = 0, unified = 0;
  // חברות אשראי חותכות שמות ארוכים, לכן גם התאמת תחילית נחשבת
  function lookup(k) {
    if (mem[k]) return mem[k];
    if (k.length < 10) return null;
    let hit = null;
    for (const mk of memKeys) {
      if (mk.length < 10) continue;
      if (mk.indexOf(k) === 0 || k.indexOf(mk) === 0) {
        if (hit && hit.cat !== mem[mk].cat) return null; // דו משמעי
        if (!hit || mem[mk].n > hit.n) hit = mem[mk];
      }
    }
    return hit;
  }
  items.forEach((it) => {
    const k = mNorm(it.description);
    if (k.length < 3) return;
    const m = lookup(k);
    if (m && m.cat && m.agree >= 0.5) {
      if (it.cat !== m.cat) { it.cat = m.cat; fromMemory++; }
      it.fromMemory = true;
    }
  });
  const groups = {};
  items.forEach((it) => {
    if (it.fromMemory) return;
    const k = mNorm(it.description);
    if (k.length >= 3) (groups[k] = groups[k] || []).push(it);
  });
  Object.values(groups).forEach((g) => {
    if (g.length < 2) return;
    const counts = {};
    let best = null, bestN = 0;
    g.forEach((it) => {
      const c = it.cat || 'other';
      counts[c] = (counts[c] || 0) + 1;
      if (counts[c] > bestN) { bestN = counts[c]; best = c; }
    });
    g.forEach((it) => { if (it.cat !== best) { it.cat = best; unified++; } });
  });
  return { fromMemory, unified, canon };
}

/** עסקים שההוצאות שלהם ב"אחר" ב-4 התקופות האחרונות (מהתקופה המוצגת) */
export function unclassifiedMerchants(viewKey) {
  return memo('unclassified|' + viewKey, () => {
    const keys = [0, 1, 2, 3].map((q) => keyAdd(viewKey, -q));
    const g = {};
    S.tx.forEach((t) => {
      if (t.type !== 'expense' || keys.indexOf(t.month) < 0 || isExcluded(t)) return;
      if ((cat('expense', t.cat).id || 'other') !== 'other') return;
      const k = mNorm(t.description || '');
      if (!k) return;
      const e = (g[k] = g[k] || { k, name: (t.description || '').trim(), sum: 0, n: 0, ids: [], last: '' });
      e.sum += absAmt(t);
      e.n++;
      e.ids.push(t.id);
      if (String(t.date) > e.last) { e.last = String(t.date); e.name = (t.description || '').trim(); }
    });
    return Object.values(g).sort((a, b) => b.sum - a.sum);
  });
}

/* ── שמות עו"ש שנשמרו הפוכים ("יבכמ" במקום "מכבי") ──
   מזהים בזהירות: ההיפוך נשמע כמו עברית תקינה (אותיות סופיות במקום), או שהשם ההפוך
   כבר מוכר באפליקציה (חיוב קבוע או בית עסק באשראי) והנוכחי לא. */
export function revCandidates() {
  return memo('revCandidates', () => {
    const known = {};
    S.rec.forEach((r) => { const c = recCore(r.description)[0]; if (c && c.length >= 3) known[c] = 1; });
    S.tx.forEach((t) => { if (getTxSrc(t) === 'credit') { const c = recCore(t.description)[0]; if (c && c.length >= 3) known[c] = 1; } });
    const ok = new Set((S.set.revOk || []).map(String));
    const out = [];
    S.tx.forEach((t) => {
      if (getTxSrc(t) === 'credit' || ok.has(String(t.id))) return;
      const d = String(t.description || '');
      if ((d.match(/[א-ת]/g) || []).length < 3) return;
      const rev = reverseKeepRuns(d);
      if (rev === d) return;
      const gain = rtlScore(rev) - rtlScore(d);
      const knownRev = recCore(rev).some((x) => known[x]), knownCur = recCore(d).some((x) => known[x]);
      if (gain >= 1 || (knownRev && !knownCur && gain >= 0)) out.push({ t, rev });
    });
    return out;
  });
}

